import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { useAuthStore } from "../store/authStore";
import { getSyncClient } from "../sync/SyncClient";
import {
  formatDollars,
  hasAnythingToImport,
  importErrorMessage,
  parseLegacyInput,
  planLocally,
  plural,
  postLegacyImport,
  previewRerunState,
  previewRows,
  readLegacyStorage,
  recordCount,
  summarizeResult,
  type LegacyBundleInput,
  type LegacyImportResponse,
  type LocalPreview,
} from "../import/legacyImport";

type Phase = "edit" | "previewing" | "ready" | "confirm" | "importing" | "done";

interface PreviewState {
  bundle: LegacyBundleInput;
  keys: string[];
  notice: string | null;
  local: LocalPreview;
  /** Authoritative server preview (with "already there" counts); null if it failed. */
  server: LegacyImportResponse | null;
}

export function SettingsImport() {
  const me = useAuthStore((s) => s.me);
  const readOnly = me?.plan.readOnly ?? false;
  const orgName = me?.org.name ?? "your account";

  const [json, setJson] = useState("");
  const [phase, setPhase] = useState<Phase>("edit");
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [result, setResult] = useState<LegacyImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  function resetFlow() {
    abortRef.current?.abort();
    setPhase("edit");
    setPreview(null);
    setResult(null);
    setError(null);
  }

  function onEdit(text: string) {
    setJson(text);
    setInfo(null);
    if (phase !== "edit" || preview || result || error) resetFlow();
  }

  async function runPreview(text: string) {
    abortRef.current?.abort();
    setResult(null);
    setError(null);
    const parsed = parseLegacyInput(text);
    if (!parsed.ok) {
      setPreview(null);
      setPhase("edit");
      setError(parsed.error);
      return;
    }
    let local: LocalPreview;
    try {
      local = planLocally(parsed.bundle);
    } catch (e) {
      setPreview(null);
      setPhase("edit");
      setError(`Couldn’t read that data: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    if (!hasAnythingToImport(local)) {
      setPreview(null);
      setPhase("edit");
      setError(
        local.counts.loadsSkippedDismissed > 0
          ? `Nothing to import — all ${plural(local.counts.loadsSkippedDismissed, "load")} in that data were dismissed in the old app.`
          : "Nothing to import — no loads, fuel, maintenance, detention or settings in that data.",
      );
      return;
    }
    const base: PreviewState = {
      bundle: parsed.bundle,
      keys: parsed.keys,
      notice: parsed.notice,
      local,
      server: null,
    };
    setPreview(base);
    setPhase("previewing");
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const server = await postLegacyImport(parsed.bundle, true, ctrl.signal);
      if (ctrl.signal.aborted) return;
      setPreview({ ...base, server });
      setPhase("ready");
    } catch (e) {
      if (ctrl.signal.aborted || (e instanceof DOMException && e.name === "AbortError")) return;
      setError(importErrorMessage(e));
      setPhase("ready");
    }
  }

  function readBrowser() {
    setInfo(null);
    const { bundle, keys } = readLegacyStorage(window.localStorage);
    if (keys.length === 0) {
      resetFlow();
      setError(
        "No old Haul Sheet data in this browser. Open this page in the same browser you used the old app in, or paste an export below.",
      );
      return;
    }
    const text = JSON.stringify(bundle, null, 2);
    setJson(text);
    setInfo(`Found ${plural(keys.length, "saved item")} from the old app in this browser.`);
    void runPreview(text);
  }

  async function confirmImport() {
    if (!preview?.server) return;
    setPhase("importing");
    setError(null);
    try {
      const res = await postLegacyImport(preview.bundle, false);
      setResult(res);
      setPhase("done");
      // Pull the new rows into the offline store so Loads / Logs show them now.
      void getSyncClient().syncNow();
    } catch (e) {
      setError(`${importErrorMessage(e)} Nothing was changed — the import runs all-or-nothing.`);
      setPhase("ready");
    }
  }

  const server = preview?.server ?? null;
  const counts = server?.counts ?? preview?.local.counts ?? null;
  const totals = server?.totals ?? preview?.local.totals ?? null;
  const rerun = counts ? previewRerunState(counts, server?.existing ?? null) : "new";
  const busy = phase === "previewing" || phase === "importing";
  const canImport = !!server && !readOnly && phase === "ready";
  const summary = result ? summarizeResult(result) : null;

  return (
    <div className="stack">
      <Link to="/settings" className="muted back-link">
        ← Settings
      </Link>
      <h1 className="screen-title">Import my old data</h1>

      {readOnly ? (
        <p className="legal-flag" role="alert">
          Your plan is read-only until billing is fixed, so importing is off. You can still
          preview. <Link to="/settings/plan">Settings → Plan</Link>
        </p>
      ) : null}

      <section className="card stack" aria-labelledby="import-source">
        <h2 id="import-source" style={{ margin: 0, fontSize: "1.05rem" }}>
          1. Get your old data
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          Loads, fuel, maintenance, detention and your cost settings from the old Haul Sheet or
          Prebook Brief. Use the same phone/browser you used before, or paste an export.
        </p>
        <div className="row">
          <Button onClick={readBrowser} disabled={busy}>
            Read this browser
          </Button>
        </div>
        <label htmlFor="legacy-json" className="muted">
          Or paste exported JSON
        </label>
        <textarea
          id="legacy-json"
          placeholder='{"haulSheetLoadLog_v1": [ … ], "haulSheetFuelLog": [ … ]}'
          value={json}
          onChange={(e) => onEdit(e.target.value)}
          rows={8}
          spellCheck={false}
          disabled={phase === "importing"}
        />
        {info ? <p className="muted" style={{ margin: 0 }}>{info}</p> : null}
        <div className="row">
          <Button
            variant="primary"
            onClick={() => void runPreview(json)}
            disabled={busy || !json.trim()}
          >
            {phase === "previewing" ? "Checking…" : "Preview"}
          </Button>
        </div>
      </section>

      {error ? (
        <p className="legal-flag card" role="alert" data-testid="import-error">
          {error}
        </p>
      ) : null}

      {preview && counts && totals && phase !== "done" ? (
        <section className="card stack" aria-labelledby="import-preview" data-testid="import-preview">
          <h2 id="import-preview" style={{ margin: 0, fontSize: "1.05rem" }}>
            2. Check what will come over
          </h2>
          {phase === "previewing" ? <p className="muted" style={{ margin: 0 }}>Checking with Haul Sheet…</p> : null}
          {preview.notice ? <p className="muted" style={{ margin: 0 }}>{preview.notice}</p> : null}
          <table className="table">
            <thead>
              <tr>
                <th scope="col">What</th>
                <th scope="col" className="num">In old data</th>
                {server ? <th scope="col" className="num">Already here</th> : null}
              </tr>
            </thead>
            <tbody>
              {previewRows(counts, server?.existing ?? null).map((r) => (
                <tr key={r.key} data-testid={`count-${r.key}`}>
                  <td>{r.label}</td>
                  <td className="num">{r.count}</td>
                  {server ? <td className="num">{r.existing ?? "—"}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
          {counts.loadsSkippedDismissed > 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              {plural(counts.loadsSkippedDismissed, "load")} you dismissed in the old app will be
              skipped.
            </p>
          ) : null}
          {counts.documentsQueued > 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              {plural(counts.documentsQueued, "fuel receipt")} will be listed on the fill-ups; the
              photos themselves stay in the old app.
            </p>
          ) : null}
          {counts.loads > 0 ? (
            <p style={{ margin: 0 }}>
              Linehaul {formatDollars(totals.linehaulCents)} · you kept{" "}
              <strong>{formatDollars(totals.keepCents)}</strong>
              {totals.legacyKeepCents > 0 && totals.legacyKeepCents !== totals.keepCents ? (
                <span className="muted">
                  {" "}
                  (old app showed {formatDollars(totals.legacyKeepCents)})
                </span>
              ) : null}
            </p>
          ) : null}
          {preview.local.settings.costs || preview.local.settings.mpg ? (
            <p className="muted" style={{ margin: 0 }}>
              Your{" "}
              {[preview.local.settings.costs ? "cost settings" : null, preview.local.settings.mpg ? "truck MPG" : null]
                .filter(Boolean)
                .join(" and ")}{" "}
              will be set from the old app.
            </p>
          ) : null}

          {server && rerun === "already" ? (
            <p className="legal-ok" style={{ margin: 0 }} data-testid="import-already">
              Already imported — everything here is in Haul Sheet. Re-running won’t add duplicates.
            </p>
          ) : null}
          {server && rerun === "partial" ? (
            <p className="muted" style={{ margin: 0 }} data-testid="import-partial">
              Some of this was imported before. Only the new items will be added — no duplicates.
            </p>
          ) : null}
          {!server && phase === "ready" ? (
            <div className="row">
              <Button onClick={() => void runPreview(json)}>Retry check</Button>
            </div>
          ) : null}

          {phase === "confirm" ? (
            <div className="stack" role="group" aria-label="Confirm import">
              <p style={{ margin: 0 }}>
                {recordCount(counts) > 0 ? (
                  <>
                    Import {plural(recordCount(counts), "record")} into <strong>{orgName}</strong>?
                    Anything already imported is skipped.
                  </>
                ) : (
                  <>
                    Apply your old settings to <strong>{orgName}</strong>?
                  </>
                )}
              </p>
              <div className="row">
                <Button variant="primary" onClick={() => void confirmImport()}>
                  Yes, import
                </Button>
                <Button variant="ghost" onClick={() => setPhase("ready")}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="row">
              <Button
                variant="primary"
                onClick={() => setPhase("confirm")}
                disabled={!canImport}
              >
                {phase === "importing"
                  ? "Importing…"
                  : rerun === "already"
                    ? "Import again (no duplicates)"
                    : "Import"}
              </Button>
            </div>
          )}
        </section>
      ) : null}

      {phase === "done" && result && summary ? (
        <section className="card stack" aria-labelledby="import-done" data-testid="import-result">
          <h2 id="import-done" style={{ margin: 0, fontSize: "1.05rem" }}>
            {summary.state === "already" ? "Nothing new" : "Import done"}
          </h2>
          <p className={summary.state === "already" ? "muted" : "legal-ok"} style={{ margin: 0 }} role="status">
            {summary.headline}
          </p>
          {result.created.defaultContractRetired ? (
            <p className="muted" style={{ margin: 0 }} data-testid="import-default-retired">
              The blank 0% “Default lease” was switched off, so new checks use your imported
              cut. Change it any time in Settings → Pay.
            </p>
          ) : null}
          {result.created.documents > 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              {plural(result.created.documents, "fuel receipt")} listed.
            </p>
          ) : null}
          <div className="row">
            <Link className="btn btn-primary" to="/loads">
              See loads
            </Link>
            <Link className="btn" to="/logs/fuel">
              Fuel log
            </Link>
            <Button
              variant="ghost"
              onClick={() => {
                setJson("");
                setInfo(null);
                resetFlow();
              }}
            >
              Import more
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
